/** ******************************************************************************************************************
 * @file Taken and modified from [convert-color](https://www.npmjs.com/package/color-convert) under an MIT license.
 * @author julian <jjensen@licorice.io>
 * @since 1.0.0
 * @date 04-02-2024
 *********************************************************************************************************************/
/* eslint-disable no-mixed-operators */
import cssKeywords from 'color-name';

const { max, min, round, cos, sin, floor, atan2, PI, sqrt } = Math;

// NOTE: conversions should only return primitive values (i.e. arrays, or
//       values that give correct `typeof` results).
//       do not use box values types (i.e. Number(), String(), etc.)

const reverseKeywords = Object.fromEntries( Object.entries( cssKeywords ).map( ([ key, value ]) => [ value, key ]) );

const conversions = {
    rgb:     { channels: 3, labels: 'rgb' },
    hsl:     { channels: 3, labels: 'hsl' },
    hsv:     { channels: 3, labels: 'hsv' },
    hwb:     { channels: 3, labels: 'hwb' },
    cmyk:    { channels: 4, labels: 'cmyk' },
    xyz:     { channels: 3, labels: 'xyz' },
    lab:     { channels: 3, labels: 'lab' },
    lch:     { channels: 3, labels: 'lch' },
    hex:     { channels: 1, labels: [ 'hex' ] },
    keyword: { channels: 1, labels: [ 'keyword' ] },
    ansi16:  { channels: 1, labels: [ 'ansi16' ] },
    ansi256: { channels: 1, labels: [ 'ansi256' ] },
    hcg:     { channels: 3, labels: [ 'h', 'c', 'g' ] },
    apple:   { channels: 3, labels: [ 'r16', 'g16', 'b16' ] },
    gray:    { channels: 1, labels: [ 'gray' ] }
};

const convert = {};

// LAB f(t) constant
const LAB_FT = ( 6 / 29 ) ** 3;

// Hide .channels and .labels properties
for ( const model of Object.keys( conversions ) )
{
    if ( !Object.hasOwn( conversions[ model ], 'channels' ) )
        throw new Error( 'missing channels property: ' + model );

    if ( !Object.hasOwn( conversions[ model ], 'labels' ) )
        throw new Error( 'missing channel labels property: ' + model );

    if ( conversions[ model ].labels.length !== conversions[ model ].channels )
        throw new Error( 'channel and label counts mismatch: ' + model );

    const { channels, labels } = conversions[ model ];
    delete conversions[ model ].channels;
    delete conversions[ model ].labels;
    Object.defineProperty( conversions[ model ], 'channels', { value: channels });
    Object.defineProperty( conversions[ model ], 'labels', { value: labels });
}

conversions.rgb.hsl = function( rgb ) {
    const r     = rgb[ 0 ] / 255;
    const g     = rgb[ 1 ] / 255;
    const b     = rgb[ 2 ] / 255;
    const min   = Math.min( r, g, b );
    const max   = Math.max( r, g, b );
    const delta = max - min;
    let h;
    let s;

    if ( max === min )
        h = 0;
    else if ( r === max )
        h = ( g - b ) / delta;
    else if ( g === max )
        h = 2 + ( b - r ) / delta;
    else if ( b === max )
        h = 4 + ( r - g ) / delta;

    h = Math.min( h * 60, 360 );

    if ( h < 0 ) h += 360;

    const l = ( min + max ) / 2;

    if ( max === min )
        s = 0;
    else if ( l <= 0.5 )
        s = delta / ( max + min );
    else
        s = delta / ( 2 - max - min );

    return [ h, s * 100, l * 100 ];
};

conversions.rgb.hsv = function( rgb ) {
    let h;
    let s;

    const r     = rgb[ 0 ] / 255;
    const g     = rgb[ 1 ] / 255;
    const b     = rgb[ 2 ] / 255;
    const v     = max( r, g, b );
    const diff  = v - min( r, g, b );
    const diffc = c => ( v - c ) / 6 / diff + 1 / 2;

    if ( diff === 0 )
    {
        h = 0;
        s = 0;
    }
    else
    {
        s          = diff / v;
        const rdif = diffc( r );
        const gdif = diffc( g );
        const bdif = diffc( b );

        if ( r === v )
            h = bdif - gdif;
        else if ( g === v )
            h = ( 1 / 3 ) + rdif - bdif;
        else if ( b === v )
            h = ( 2 / 3 ) + gdif - rdif;

        if ( h < 0 )
            h += 1;
        else if ( h > 1 )
            h -= 1;
    }

    return [ h * 360, s * 100, v * 100 ];
};

conversions.rgb.hwb = function( rgb ) {
    const [ r, g, b ] = rgb;
    const h           = conversions.rgb.hsl( rgb )[ 0 ];
    const w           = 1 / 255 * min( r, min( g, b ) );

    let s = 1 - 1 / 255 * max( r, max( g, b ) );

    return [ h, w * 100, s * 100 ];
};

conversions.rgb.cmyk = function([ r, g, b ]) {
    r /= 255;
    g /= 255;
    b /= 255;

    const k = min( 1 - r, 1 - g, 1 - b );
    const c = ( 1 - r - k ) / ( 1 - k ) || 0;
    const m = ( 1 - g - k ) / ( 1 - k ) || 0;
    const y = ( 1 - b - k ) / ( 1 - k ) || 0;

    return [ c * 100, m * 100, y * 100, k * 100 ];
};

/*
 See https://en.m.wikipedia.org/wiki/Euclidean_distance#Squared_Euclidean_distance
 */
function comparativeDistance([ x, y, z ], [ s, t, u ])
{
    return (
        ( x - s ) * ( x - s ) +
        ( y - t ) * ( y - t ) +
        ( z - u ) * ( z - u )
    );
}

conversions.rgb.keyword = function( rgb ) {
    const reversed = reverseKeywords[ rgb ];
    if ( reversed )
        return reversed;

    let currentClosestDistance = Number.POSITIVE_INFINITY;
    let currentClosestKeyword;

    for ( const keyword of Object.keys( cssKeywords ) )
    {
        const value = cssKeywords[ keyword ];

        // Compute comparative distance
        const distance = comparativeDistance( rgb, value );

        // Check if it's less, if so set as closest
        if ( distance < currentClosestDistance )
        {
            currentClosestDistance = distance;
            currentClosestKeyword  = keyword;
        }
    }

    return currentClosestKeyword;
};

conversions.keyword.rgb = keyword => cssKeywords[ keyword ];

conversions.rgb.xyz = function([ r, g, b ]) {
    r /= 255;
    g /= 255;
    b /= 255;

    // Assume sRGB
    r = r > 0.04045 ? ( ( ( r + 0.055 ) / 1.055 ) ** 2.4 ) : ( r / 12.92 );
    g = g > 0.04045 ? ( ( ( g + 0.055 ) / 1.055 ) ** 2.4 ) : ( g / 12.92 );
    b = b > 0.04045 ? ( ( ( b + 0.055 ) / 1.055 ) ** 2.4 ) : ( b / 12.92 );

    const x = ( r * 0.4124564 ) + ( g * 0.3575761 ) + ( b * 0.1804375 );
    const y = ( r * 0.2126729 ) + ( g * 0.7151522 ) + ( b * 0.072175 );
    const z = ( r * 0.0193339 ) + ( g * 0.119192 ) + ( b * 0.9503041 );

    return [ x * 100, y * 100, z * 100 ];
};

conversions.rgb.lab = function( rgb ) {
    const xyz = conversions.rgb.xyz( rgb );
    let x     = xyz[ 0 ];
    let y     = xyz[ 1 ];
    let z     = xyz[ 2 ];

    x /= 95.047;
    y /= 100;
    z /= 108.883;

    x = x > LAB_FT ? ( x ** ( 1 / 3 ) ) : ( 7.787 * x ) + ( 16 / 116 );
    y = y > LAB_FT ? ( y ** ( 1 / 3 ) ) : ( 7.787 * y ) + ( 16 / 116 );
    z = z > LAB_FT ? ( z ** ( 1 / 3 ) ) : ( 7.787 * z ) + ( 16 / 116 );

    const l = ( 116 * y ) - 16;
    const a = 500 * ( x - y );
    const b = 200 * ( y - z );

    return [ l, a, b ];
};

conversions.hsl.rgb = function([ h, s, l ]) {
    h /= 360;
    s /= 100;
    l /= 100;
    let val;

    if ( s === 0 )
    {
        val = l * 255;
        return [ val, val, val ];
    }

    let t2 = l < 0.5 ? l * ( 1 + s ) : l + s - l * s;

    const t1  = 2 * l - t2;
    const rgb = [ 0, 0, 0 ];

    for ( let i = 0; i < 3; i++ )
    {
        let t3 = h + 1 / 3 * -( i - 1 );
        if ( t3 < 0 ) t3++;
        if ( t3 > 1 ) t3--;

        if ( 6 * t3 < 1 )
            val = t1 + ( t2 - t1 ) * 6 * t3;
        else if ( 2 * t3 < 1 )
            val = t2;
        else if ( 3 * t3 < 2 )
            val = t1 + ( t2 - t1 ) * ( 2 / 3 - t3 ) * 6;
        else
            val = t1;

        rgb[ i ] = val * 255;
    }

    return rgb;
};

conversions.hsl.hsv = function([ h, s, l ]) {
    s /= 100;
    l /= 100;
    let smin   = s;
    const lmin = max( l, 0.01 );

    l *= 2;
    s *= l <= 1 ? l : 2 - l;
    smin *= lmin <= 1 ? lmin : 2 - lmin;
    const v  = ( l + s ) / 2;
    const sv = l === 0 ? ( 2 * smin ) / ( lmin + smin ) : ( 2 * s ) / ( l + s );

    return [ h, sv * 100, v * 100 ];
};

conversions.hsv.rgb = function([ h, s, v ]) {
    h /= 60;
    s /= 100;
    v /= 100;
    const hi = floor( h ) % 6;

    const f = h - floor( h );
    const p = 255 * v * ( 1 - s );
    const q = 255 * v * ( 1 - ( s * f ) );
    const t = 255 * v * ( 1 - ( s * ( 1 - f ) ) );
    v *= 255;

    switch ( hi )
    {
        case 0:
            return [ v, t, p ];
        case 1:
            return [ q, v, p ];
        case 2:
            return [ p, v, t ];
        case 3:
            return [ p, q, v ];
        case 4:
            return [ t, p, v ];
        case 5:
            return [ v, p, q ];
    }
};

conversions.hsv.hsl = function([ h, s, v ]) {
    s /= 100;
    v /= 100;
    const vmin = max( v, 0.01 );
    const lmin = ( 2 - s ) * vmin;
    let sl     = s * vmin;
    let l      = ( 2 - s ) * v;

    sl /= ( lmin <= 1 ) ? lmin : 2 - lmin;
    sl = sl || 0;
    l /= 2;

    return [ h, sl * 100, l * 100 ];
};

// http://dev.w3.org/csswg/css-color/#hwb-to-rgb
conversions.hwb.rgb = function([ h, w, bl ]) {
    h /= 360;
    w /= 100;
    bl /= 100;
    const ratio = w + bl;

    // `W + bl` can't be > 1
    if ( ratio > 1 )
    {
        w /= ratio;
        bl /= ratio;
    }

    const i = floor( 6 * h );
    const v = 1 - bl;
    let f   = 6 * h - i;

    if ( i & 1 ) f = 1 - f;

    const n = w + f * ( v - w ); // Linear interpolation

    let r, g, b;

    /* eslint-disable max-statements-per-line,no-multi-spaces */
    switch ( i )
    {
        default:
        case 6:
        case 0:
            [ r, g, b ] = [ v, n, w ];
            break;
        case 1:
            [ r, g, b ] = [ n, v, w ];
            break;
        case 2:
            [ r, g, b ] = [ w, v, n ];
            break;
        case 3:
            [ r, g, b ] = [ w, n, v ];
            break;
        case 4:
            [ r, g, b ] = [ n, w, v ];
            break;
        case 5:
            [ r, g, b ] = [ v, w, n ];
            break;
    }
    /* eslint-enable max-statements-per-line,no-multi-spaces */

    return [ r * 255, g * 255, b * 255 ];
};

conversions.cmyk.rgb = function([ c, m, y, k ]) {
    c /= 100;
    m /= 100;
    y /= 100;
    k /= 100;

    const r = 1 - min( 1, c * ( 1 - k ) + k );
    const g = 1 - min( 1, m * ( 1 - k ) + k );
    const b = 1 - min( 1, y * ( 1 - k ) + k );

    return [ r * 255, g * 255, b * 255 ];
};

conversions.xyz.rgb = function([ x, y, z ]) {
    x /= 100;
    y /= 100;
    z /= 100;

    let r = ( x * 3.2404542 ) + ( y * -1.5371385 ) + ( z * -0.4985314 );
    let g = ( x * -0.969266 ) + ( y * 1.8760108 ) + ( z * 0.041556 );
    let b = ( x * 0.0556434 ) + ( y * -0.2040259 ) + ( z * 1.0572252 );

    // Assume sRGB
    r = r > 0.0031308
        ? ( ( 1.055 * ( r ** ( 1.0 / 2.4 ) ) ) - 0.055 )
        : r * 12.92;

    g = g > 0.0031308
        ? ( ( 1.055 * ( g ** ( 1.0 / 2.4 ) ) ) - 0.055 )
        : g * 12.92;

    b = b > 0.0031308
        ? ( ( 1.055 * ( b ** ( 1.0 / 2.4 ) ) ) - 0.055 )
        : b * 12.92;

    return [ min( max( 0, r ), 1 ) * 255, min( max( 0, g ), 1 ) * 255, min( max( 0, b ), 1 ) * 255 ];
};

conversions.xyz.lab = function([ x, y, z ]) {
    x /= 95.047;
    y /= 100;
    z /= 108.883;

    x = x > LAB_FT ? ( x ** ( 1 / 3 ) ) : ( 7.787 * x ) + ( 16 / 116 );
    y = y > LAB_FT ? ( y ** ( 1 / 3 ) ) : ( 7.787 * y ) + ( 16 / 116 );
    z = z > LAB_FT ? ( z ** ( 1 / 3 ) ) : ( 7.787 * z ) + ( 16 / 116 );

    return [ ( 116 * y ) - 16, 500 * ( x - y ), 200 * ( y - z ) ];
};

conversions.lab.xyz = function([ l, a, b ]) {

    let y = ( l + 16 ) / 116;
    let x = a / 500 + y;
    let z = y - b / 200;

    const y2 = y ** 3;
    const x2 = x ** 3;
    const z2 = z ** 3;
    y        = y2 > LAB_FT ? y2 : ( y - 16 / 116 ) / 7.787;
    x        = x2 > LAB_FT ? x2 : ( x - 16 / 116 ) / 7.787;
    z        = z2 > LAB_FT ? z2 : ( z - 16 / 116 ) / 7.787;

    // Illuminant D65 XYZ Tristrimulus Values
    // https://en.wikipedia.org/wiki/CIE_1931_color_space
    return [ x * 95.047, y * 100, z * 108.883 ];
};

conversions.lab.lch = function([ l, a, b ]) {
    let h = atan2( b, a ) * 360 / 2 / PI;

    return [ l, sqrt( a * a + b * b ), h < 0 ? h + 360 : h ];
};

conversions.lch.lab = function([ l, c, h ]) {
    const hr = h / 360 * 2 * PI;

    return [ l, c * cos( hr ), c * sin( hr ) ];
};

conversions.rgb.ansi16 = function( args, saturation = null ) {
    const [ r, g, b ] = args;
    let value         = saturation === null
        ? conversions.rgb.hsv( args )[ 2 ]
        : saturation; // Hsv -> ansi16 optimization

    value = round( value / 50 );

    if ( value === 0 ) return 30;

    let ansi = 30 +
               ( ( round( b / 255 ) << 2 ) |
                 ( round( g / 255 ) << 1 ) |
                 round( r / 255 ) );

    if ( value === 2 ) ansi += 60;

    return ansi;
};

// Optimization here; we already know the value and don't need to get it converted for us.
conversions.hsv.ansi16 = args => conversions.rgb.ansi16( conversions.hsv.rgb( args ), args[ 2 ]);

conversions.rgb.ansi256 = function([ r, g, b ]) {
    // We use the extended greyscale palette here, except for
    // black and white. Normal palette only has 4 greyscale shades.
    if ( r >> 4 === g >> 4 && g >> 4 === b >> 4 )
    {
        if ( r < 8 ) return 16;
        if ( r > 248 ) return 231;

        return round( ( ( r - 8 ) / 247 ) * 24 ) + 232;
    }

    return 16 +
           ( 36 * round( r / 255 * 5 ) ) +
           ( 6 * round( g / 255 * 5 ) ) +
           round( b / 255 * 5 );
};

conversions.ansi16.rgb = function([ args ]) {
    let color = args % 10;

    // Handle greyscale
    if ( color === 0 || color === 7 )
    {
        if ( args > 50 ) color += 3.5;

        color = color / 10.5 * 255;

        return [ color, color, color ];
    }

    const mult = ( ~~( args > 50 ) + 1 ) * 0.5;

    return [
        ( ( color & 1 ) * mult ) * 255,
        ( ( ( color >> 1 ) & 1 ) * mult ) * 255,
        ( ( ( color >> 2 ) & 1 ) * mult ) * 255
    ];
};

conversions.ansi256.rgb = function( args ) {
    args = args[ 0 ];

    // Handle greyscale
    if ( args >= 232 )
    {
        const c = ( args - 232 ) * 10 + 8;
        return [ c, c, c ];
    }

    args -= 16;

    let rem;

    return [
        floor( args / 36 ) / 5 * 255,
        floor( ( rem = args % 36 ) / 6 ) / 5 * 255,
        ( rem % 6 ) / 5 * 255
    ];
};

conversions.rgb.hex = function([ r, g, b ]) {
    const integer = ( ( round( r ) & 0xFF ) << 16 ) +
                    ( ( round( g ) & 0xFF ) << 8 ) +
                    ( round( b ) & 0xFF );

    const string = integer.toString( 16 ).toUpperCase();
    return '000000'.substring( string.length ) + string;
};

conversions.hex.rgb = function( args ) {
    const match = args.toString( 16 ).match( /[a-f0-9]{6}|[a-f0-9]{3}/i );

    if ( !match ) return [ 0, 0, 0 ];

    let colorString = match[ 0 ];

    if ( match[ 0 ].length === 3 )
        colorString = colorString.split( '' ).map( char => char + char ).join( '' );

    const integer = parseInt( colorString, 16 );
    const r       = ( integer >> 16 ) & 0xFF;
    const g       = ( integer >> 8 ) & 0xFF;
    const b       = integer & 0xFF;

    return [ r, g, b ];
};

conversions.rgb.hcg = function([ r, g, b ]) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max    = max( max( r, g ), b );
    const min    = min( min( r, g ), b );
    const chroma = ( max - min );
    let hue;

    let grayscale = chroma < 1 ? min / ( 1 - chroma ) : 0;

    if ( chroma <= 0 )
        hue = 0;
    else if ( max === r )
        hue = ( ( g - b ) / chroma ) % 6;
    else if ( max === g )
        hue = 2 + ( b - r ) / chroma;
    else
        hue = 4 + ( r - g ) / chroma;

    hue /= 6;
    hue %= 1;

    return [ hue * 360, chroma * 100, grayscale * 100 ];
};

conversions.hsl.hcg = function([ h, s, l ]) {
    s /= 100;
    l /= 100;

    const c = l < 0.5 ? ( 2.0 * s * l ) : ( 2.0 * s * ( 1.0 - l ) );

    let f = c < 1.0 ? ( l - 0.5 * c ) / ( 1.0 - c ) : 0;

    return [ h, c * 100, f * 100 ];
};

conversions.hsv.hcg = function([ h, s, v ]) {
    s /= 100;
    v /= 100;

    const c = s * v;
    let f   = c < 1.0 ? ( v - c ) / ( 1 - c ) : 0;

    return [ h, c * 100, f * 100 ];
};

conversions.hcg.rgb = function([ h, c, g ]) {
    h /= 360;
    c /= 100;
    g /= 100;

    if ( c === 0.0 )
        return [ g * 255, g * 255, g * 255 ];

    let pure;
    const hi = ( h % 1 ) * 6;
    const v  = hi % 1;
    const w  = 1 - v;

    /* eslint-disable max-statements-per-line */
    switch ( floor( hi ) )
    {
        case 0:
            pure = [ 1, v, 0 ];
            break;
        case 1:
            pure = [ w, 1, 0 ];
            break;
        case 2:
            pure = [ 0, 1, v ];
            break;
        case 3:
            pure = [ 0, w, 1 ];
            break;
        case 4:
            pure = [ v, 0, 1 ];
            break;
        default:
            pure = [ 1, 0, w ];
    }
    /* eslint-enable max-statements-per-line */

    let mg = ( 1.0 - c ) * g;

    return [
        ( c * pure[ 0 ] + mg ) * 255,
        ( c * pure[ 1 ] + mg ) * 255,
        ( c * pure[ 2 ] + mg ) * 255
    ];
};

conversions.hcg.hsv = function([ h, c, g ]) {
    c /= 100;
    g /= 100;

    const v = c + g * ( 1.0 - c );
    let f   = v > 0.0 ? c / v : 0;

    return [ h, f * 100, v * 100 ];
};

conversions.hcg.hsl = function([ h, c, g ]) {
    c /= 100;
    g /= 100;

    const l = g * ( 1.0 - c ) + 0.5 * c;
    let s   = 0;

    if ( l > 0.0 && l < 0.5 )
        s = c / ( 2 * l );
    else if ( l >= 0.5 && l < 1.0 )
        s = c / ( 2 * ( 1 - l ) );

    return [ h, s * 100, l * 100 ];
};

conversions.hcg.hwb = function([ h, c, g ]) {
    c /= 100;
    g /= 100;

    const v = c + g * ( 1.0 - c );

    return [ h, ( v - c ) * 100, ( 1 - v ) * 100 ];
};

conversions.hwb.hcg = function([ h, w, b ]) {
    w /= 100;
    b /= 100;
    const v = 1 - b;
    const c = v - w;
    let g   = c < 1 ? ( v - c ) / ( 1 - c ) : 0;

    return [ h, c * 100, g * 100 ];
};

conversions.apple.rgb = apple =>
    [ ( apple[ 0 ] / 65535 ) * 255, ( apple[ 1 ] / 65535 ) * 255, ( apple[ 2 ] / 65535 ) * 255 ];

conversions.rgb.apple = rgb =>
    [ ( rgb[ 0 ] / 255 ) * 65535, ( rgb[ 1 ] / 255 ) * 65535, ( rgb[ 2 ] / 255 ) * 65535 ];

conversions.gray.rgb = args => [ args[ 0 ] / 100 * 255, args[ 0 ] / 100 * 255, args[ 0 ] / 100 * 255 ];

conversions.gray.hsl = args => [ 0, 0, args[ 0 ] ];

conversions.gray.hsv = conversions.gray.hsl;

conversions.gray.hwb = gray => [ 0, 100, gray[ 0 ] ];

conversions.gray.cmyk = gray => [ 0, 0, 0, gray[ 0 ] ];

conversions.gray.lab = gray => [ gray[ 0 ], 0, 0 ];

conversions.gray.hex = function( gray ) {
    const val     = round( gray[ 0 ] / 100 * 255 ) & 0xFF;
    const integer = ( val << 16 ) + ( val << 8 ) + val;

    const string = integer.toString( 16 ).toUpperCase();
    return '000000'.substring( string.length ) + string;
};

conversions.rgb.gray = ([ r, g, b ]) => [ ( r + g + b ) / 3 / 255 * 100 ];

// ROUTE MODULE route.,js

/*
 This function routes a model to all other models.

 All functions that are routed have a property `.conversion` attached
 to the returned synthetic function. This property is an array
 of strings, each with the steps in between the 'from' and 'to'
 color models (inclusive).

 Conversions that are not possible simply are not included.
 */

function buildGraph()
{
    const graph  = {};
    // https://jsperf.com/object-keys-vs-for-in-with-closure/3
    const models = Object.keys( conversions );

    for ( let len = models.length, i = 0; i < len; i++ )
    {
        graph[ models[ i ] ] = {
            // http://jsperf.com/1-vs-infinity
            // micro-opt, but this is trivial.
            distance: -1,
            parent:   null
        };
    }

    return graph;
}

// https://en.wikipedia.org/wiki/Breadth-first_search
function deriveBFS( fromModel )
{
    const graph = buildGraph();
    const queue = [ fromModel ]; // Unshift -> queue -> pop

    graph[ fromModel ].distance = 0;

    while ( queue.length )
    {
        const current   = queue.pop();
        const adjacents = Object.keys( conversions[ current ]);

        for ( let len = adjacents.length, i = 0; i < len; i++ )
        {
            const adjacent = adjacents[ i ];
            const node     = graph[ adjacent ];

            if ( node.distance === -1 )
            {
                node.distance = graph[ current ].distance + 1;
                node.parent   = current;
                queue.unshift( adjacent );
            }
        }
    }

    return graph;
}

const link = ( from, to ) => args => to( from( args ) );

function wrapConversion( toModel, graph )
{
    const path = [ graph[ toModel ].parent, toModel ];
    let fn     = conversions[ graph[ toModel ].parent ][ toModel ];

    let cur = graph[ toModel ].parent;
    while ( graph[ cur ].parent )
    {
        path.unshift( graph[ cur ].parent );
        fn  = link( conversions[ graph[ cur ].parent ][ cur ], fn );
        cur = graph[ cur ].parent;
    }

    fn.conversion = path;
    return fn;
}

function route( fromModel )
{
    const graph      = deriveBFS( fromModel );
    const conversion = {};

    const models = Object.keys( graph );
    for ( let len = models.length, i = 0; i < len; i++ )
    {
        const toModel = models[ i ];
        const node    = graph[ toModel ];

        if ( node.parent === null )
            continue;   // No possible conversion, or this node is the source model.

        conversion[ toModel ] = wrapConversion( toModel, graph );
    }

    return conversion;
}

const models = Object.keys( conversions );

function wrapRaw( fn )
{
    const wrappedFn = function( ...args ) {
        const arg0 = args[ 0 ];
        if ( arg0 == null ) return arg0;

        if ( arg0.length > 1 )
            args = arg0;

        return fn( args );
    };

    // Preserve .conversion property if there is one
    if ( Object.hasOwn( fn, 'conversion' ) )
        wrappedFn.conversion = fn.conversion;

    return wrappedFn;
}

function wrapRounded( fn )
{
    const wrappedFn = function( ...args ) {
        const arg0 = args[ 0 ];

        if ( arg0 == null ) return arg0;

        if ( arg0.length > 1 ) args = arg0;

        const result = fn( args );

        // We're assuming the result is an array here.
        // See notice in conversions.js; don't use box types
        // in conversion functions.
        if ( Array.isArray( result ) )
        {
            for ( let len = result.length, i = 0; i < len; i++ )
                result[ i ] = round( result[ i ]);
        }

        return result;
    };

    // Preserve .conversion property if there is one
    if ( Object.hasOwn( fn, 'conversion' ) )
        wrappedFn.conversion = fn.conversion;

    return wrappedFn;
}

models.forEach( fromModel => {
    convert[ fromModel ] = {};

    Object.defineProperty( convert[ fromModel ], 'channels', { value: conversions[ fromModel ].channels });
    Object.defineProperty( convert[ fromModel ], 'labels', { value: conversions[ fromModel ].labels });

    const routes      = route( fromModel );
    const routeModels = Object.keys( routes );

    routeModels.forEach( toModel => {
        const fn = routes[ toModel ];

        convert[ fromModel ][ toModel ]     = wrapRounded( fn );
        convert[ fromModel ][ toModel ].raw = wrapRaw( fn );
    });
});


export { convert as colorConvert };
